import { useState, useEffect, useRef } from "react";
import type { IngestionStatus } from "../hooks/useIngestionStatus.js";

interface IngestionBannerProps {
	status: IngestionStatus | null;
	isSyncStarting?: boolean;
}

/** Determines which visual state to render based on ingestion status. */
type BannerVariant = "starting" | "active" | "complete" | "warning" | "stale";

function getBannerVariant(
	status: IngestionStatus | null,
	isSyncStarting: boolean,
): BannerVariant | null {
	// Show "starting" when sync has been initiated but real status hasn't arrived yet.
	if (isSyncStarting && (status === null || !status.hasPendingDocuments)) return "starting";
	if (status === null) return null;
	// Stale takes priority over generic active to surface the staleness signal.
	if (status.isStale && status.hasPendingDocuments) return "stale";
	if (status.hasPendingDocuments) return "active";
	if (status.failed > 0) return "warning";
	// Only show "complete" when there are actually documents indexed.
	if (!status.hasPendingDocuments && status.total > 0 && status.failed === 0) return "complete";
	return null;
}

/**
 * Dismissible banner displayed at the top of the chat area to communicate
 * the current document ingestion state for the selected project.
 *
 * States:
 *  - starting : Blue pulsing bar — sync has been triggered, awaiting first status poll.
 *  - active   : Blue bar with animated pulse dot — documents are indexing.
 *  - complete : Green bar — indexing complete, auto-dismisses after 5 seconds.
 *  - warning  : Amber bar — some documents failed to index, with collapsible file list.
 *  - stale    : Grey bar — ingestion appears stalled (no activity for >15 min).
 */
export default function IngestionBanner({ status, isSyncStarting = false }: IngestionBannerProps) {
	const [dismissed, setDismissed] = useState(false);
	const [fading, setFading] = useState(false);
	const [showFailedList, setShowFailedList] = useState(false);
	const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const fadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const variant = getBannerVariant(status, isSyncStarting);

	// Reset dismissed state whenever the variant changes significantly,
	// so a new "active" or "warning" state resurfaces the banner.
	const prevVariantRef = useRef<BannerVariant | null>(null);
	useEffect(() => {
		if (variant !== prevVariantRef.current) {
			prevVariantRef.current = variant;
			setDismissed(false);
			setFading(false);
			// Collapse the failed list whenever the variant changes.
			setShowFailedList(false);
		}
	}, [variant]);

	// Auto-dismiss the "complete" banner after 5 seconds with a fade.
	useEffect(() => {
		if (variant !== "complete") return;

		autoDismissRef.current = setTimeout(() => {
			setFading(true);
			fadeRef.current = setTimeout(() => {
				setDismissed(true);
				setFading(false);
			}, 400); // match CSS transition duration
		}, 5_000);

		return () => {
			if (autoDismissRef.current !== null) clearTimeout(autoDismissRef.current);
			if (fadeRef.current !== null) clearTimeout(fadeRef.current);
		};
	}, [variant]);

	if (!variant || dismissed) return null;

	const variantClass = `ingestion-banner--${variant}`;

	// Determine whether the failed-files toggle should be shown.
	const hasFailedFileDetails =
		variant === "warning" &&
		status !== null &&
		status.failedFiles.length > 0;

	return (
		<div
			className={`ingestion-banner ${variantClass}${fading ? " ingestion-banner--fading" : ""}`}
			role="status"
			aria-live="polite"
			aria-atomic="true"
		>
			<div className="ingestion-banner-body">
				{variant === "starting" && (
					<>
						<div className="ingestion-banner-row">
							<span className="ingestion-banner-pulse" aria-hidden="true" />
							<span>Starting sync&hellip;</span>
						</div>
						<div
							className="ingestion-progress-track"
							role="progressbar"
							aria-valuetext="Starting sync"
							aria-label="Sync starting"
						>
							<div className="ingestion-progress-fill ingestion-progress-fill--indeterminate" />
						</div>
					</>
				)}

				{variant === "active" && (
					<>
						<div className="ingestion-banner-row">
							<span className="ingestion-banner-pulse" aria-hidden="true" />
							<span>
								Indexing documents:{" "}
								<strong>{status!.completed}</strong> of{" "}
								<strong>{status!.total}</strong> completed
								{status!.pending > 0 && (
									<> ({status!.pending} pending)</>
								)}
							</span>
						</div>
						{status!.total > 0 && (
							<div
								className="ingestion-progress-track"
								role="progressbar"
								aria-valuenow={status!.completed}
								aria-valuemin={0}
								aria-valuemax={status!.total}
								aria-label={`Indexing progress: ${status!.completed} of ${status!.total} documents`}
							>
								{status!.completed > 0 && (
									<div
										className="ingestion-progress-fill"
										style={{ width: `${(status!.completed / status!.total) * 100}%` }}
									>
										{status!.failed > 0 && (
											<div
												className="ingestion-progress-failed"
												style={{ width: `${(status!.failed / status!.completed) * 100}%` }}
											/>
										)}
									</div>
								)}
							</div>
						)}
					</>
				)}

				{variant === "complete" && (
					<>
						<span className="ingestion-banner-icon" aria-hidden="true">&#10003;</span>
						<span>
							Document indexing complete.{" "}
							<strong>{status!.total}</strong>{" "}
							{status!.total === 1 ? "document" : "documents"} indexed.
						</span>
					</>
				)}

				{variant === "warning" && (
					<>
						<div className="ingestion-banner-row">
							<span className="ingestion-banner-icon" aria-hidden="true">&#9888;</span>
							<span>
								Some documents failed to index ({status!.failed} failed).{" "}
								<strong>{status!.completed}</strong>{" "}
								{status!.completed === 1 ? "document" : "documents"} available.
							</span>
						</div>
						{hasFailedFileDetails && (
							<div className="failed-files-section">
								<button
									type="button"
									className="failed-files-toggle"
									aria-expanded={showFailedList}
									onClick={() => setShowFailedList((prev) => !prev)}
								>
									{showFailedList ? "Hide failed files" : "Show failed files"}
								</button>
								{showFailedList && (
									<ul className="failed-files-list" aria-label="Failed files">
										{status!.failedFiles.map((file, index) => (
											<li key={index} className="failed-file-item">
												<span className="failed-file-name">{file.name}</span>
												<span className="failed-file-error">
													{file.error ?? "No error detail recorded"}
												</span>
											</li>
										))}
									</ul>
								)}
							</div>
						)}
					</>
				)}

				{variant === "stale" && (
					<>
						<span className="ingestion-banner-icon" aria-hidden="true">&#8987;</span>
						<span>
							Ingestion may be stalled. Last activity over 15 minutes ago.
						</span>
					</>
				)}
			</div>

			<button
				type="button"
				className="ingestion-banner-dismiss"
				aria-label="Dismiss ingestion status"
				onClick={() => setDismissed(true)}
			>
				&times;
			</button>
		</div>
	);
}
